import { HeaderToolbar } from "@xola/ui-kit";
import { PurchaseItemStatus } from "common";
import { orderBy } from "lodash";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { ExperiencePurchaseItem, Purchase, PurchaseItem, PurchaseItemObject } from "types";
import { useDebouncedCallback } from "use-debounce";
import { useCollection } from "../../../hooks/useCollection";
import { useSearchAutoFocus } from "../../../hooks/useSearchAutoFocus";
import { ErrorBoundary } from "../../ErrorBoundary";
import { PurchaseNotesModal } from "../../PurchaseModification/PurchaseNotesModal";
import { DashboardSearchItem } from "./DashboardSearchItem";

export const DashboardSearch = () => {
    const history = useHistory();
    const shouldAutoFocus = useSearchAutoFocus();
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(null);
    const [selectedPurchaseItem, setSelectedPurchaseItem] = useState<ExperiencePurchaseItem | null>(null);
    const [showPurchaseNotesModal, setShowPurchaseNotesModal] = useState<boolean>(false);
    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
    const [pinnedItemId, setPinnedItemId] = useState<ID | null>(null);
    const minChars = 3;

    const { data: purchases, isLoading } = useCollection<Purchase>(
        "purchases",
        { dashboard: searchTerm, items_object: PurchaseItemObject.EXPERIENCE, limit: 20 },
        { enabled: searchTerm.length >= minChars },
    );

    const sortedPurchases = orderBy(purchases, ["updatedAt"], ["desc"]);
    const items = getItems(sortedPurchases, pinnedItemId);

    // The menu reports closed for a moment between the cancel and refund modals, so unpinning right away would
    // still unmount the row mid-flow.
    const unpinItem = useDebouncedCallback(() => setPinnedItemId(null), 200);

    const handleSubMenuOpenChange = (isSubMenuOpen: boolean, item: ExperiencePurchaseItem) => {
        setTimeout(() => setIsSubMenuOpen(isSubMenuOpen), 200);

        if (isSubMenuOpen) {
            unpinItem.cancel();
            setPinnedItemId(item.id);
            return;
        }

        unpinItem();
    };

    const handleSelect = ({ purchase, item }: { purchase: Purchase; item: ExperiencePurchaseItem }) => {
        history.push(`/purchases/${purchase.id}/${item.id}`);
    };

    const handlePurchaseNotesClick = (purchase: Purchase, item: ExperiencePurchaseItem) => {
        setSelectedPurchase(purchase);
        setSelectedPurchaseItem(item);
        setShowPurchaseNotesModal(true);
    };

    const togglePurchaseNotesModal = () => {
        setShowPurchaseNotesModal(!showPurchaseNotesModal);
    };

    return (
        <span className="flex-1">
            <HeaderToolbar.Search
                autoFocus={shouldAutoFocus}
                shouldDestroyOnClose={false}
                shouldStayOpen={isSubMenuOpen}
                items={items}
                defaultValue={searchTerm}
                placeholder="Name, email, phone or ID"
                isLoading={isLoading}
                minChars={minChars}
                onSelect={handleSelect}
                onChange={setSearchTerm}
            >
                {/* TODO: Check this ESLint bug */}
                {/* eslint-disable-next-line react/no-unused-prop-types */}
                {({ purchase, item }: { purchase: Purchase; item: ExperiencePurchaseItem }, isActive: boolean) => (
                    <ErrorBoundary fallbackMessage={`Error rendering purchase for ${purchase.customerName}`}>
                        <DashboardSearchItem
                            purchase={purchase}
                            item={item}
                            isActive={isActive}
                            onPurchaseNotesClick={handlePurchaseNotesClick}
                            onMenuOpenChange={handleSubMenuOpenChange}
                        />
                    </ErrorBoundary>
                )}
            </HeaderToolbar.Search>
            <PurchaseNotesModal
                purchase={selectedPurchase!}
                purchaseItem={selectedPurchaseItem as PurchaseItem}
                isOpen={showPurchaseNotesModal}
                onClose={togglePurchaseNotesModal}
            />
        </span>
    );
};

// A row's menu owns the modals that follow a cancel (refund, unassign guide), so the item whose menu is open stays
// listed after a real-time update marks it cancelled.
const getItems = (purchases: Purchase[] = [], pinnedItemId: ID | null) => {
    const items = [];

    for (const purchase of purchases) {
        for (const item of purchase.items) {
            if (item.status !== PurchaseItemStatus.CANCELED || item.id === pinnedItemId) {
                items.push({ purchase, item });
            }
        }
    }

    return items;
};
